import type { ClassSummary, SchoolSummary } from '@school-erp/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import { Notice, errorMessage, primaryButton, selectClass } from '../../components/common';
import { TextField } from '../../components/TextField';
import { t } from '../../i18n';
import { formatMinor, parseMajor } from '../../lib/money';

interface Head {
  id: string;
  name: string;
  incomeAccountCode: string;
  isActive: boolean;
}
interface Item {
  id: string;
  classId: string;
  className: string;
  feeHeadId: string;
  feeHeadName: string;
  amountMinor: number;
  dueDate: string;
}

export function StructureTab({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const manage = can('fees.manage', schoolId);
  const [headName, setHeadName] = useState('');
  const [item, setItem] = useState({ classId: '', feeHeadId: '', amount: '', dueDate: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);

  const school = useQuery({
    queryKey: ['schools', schoolId],
    queryFn: () => api<SchoolSummary>(`/schools/${schoolId}`),
  });
  const currency = school.data?.currencyCode ?? 'USD';
  const heads = useQuery({
    queryKey: ['fees', schoolId, 'heads'],
    queryFn: () => api<Head[]>(`/schools/${schoolId}/fee-heads`),
  });
  const items = useQuery({
    queryKey: ['fees', schoolId, 'items'],
    queryFn: () => api<Item[]>(`/schools/${schoolId}/fee-structure`),
  });
  const classes = useQuery({
    queryKey: ['classes', schoolId],
    queryFn: () => api<ClassSummary[]>(`/schools/${schoolId}/classes`),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['fees', schoolId] });
  const fail = (e: unknown) => setMessage(errorMessage(e));

  const addHead = useMutation({
    mutationFn: (name: string) =>
      api(`/schools/${schoolId}/fee-heads`, { method: 'POST', body: { name } }),
    onSuccess: () => {
      setHeadName('');
      setMessage(null);
      refresh();
    },
    onError: fail,
  });
  const addItem = useMutation({
    mutationFn: (body: object) =>
      api(`/schools/${schoolId}/fee-structure`, { method: 'POST', body }),
    onSuccess: () => {
      setMessage(null);
      refresh();
    },
    onError: fail,
  });
  const removeItem = useMutation({
    mutationFn: (id: string) =>
      api(`/schools/${schoolId}/fee-structure/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
    onError: fail,
  });

  return (
    <section aria-label={t('fees.tab.structure')} className="space-y-6">
      {message && <Notice kind="error">{message}</Notice>}

      <div className="space-y-3">
        <h2 className="text-lg font-semibold">{t('fees.heads')}</h2>
        {heads.data?.length === 0 && <p className="text-slate-600">{t('fees.noHeads')}</p>}
        <ul className="flex flex-wrap gap-2 text-sm">
          {heads.data?.map((h) => (
            <li key={h.id} className="rounded border border-slate-200 bg-white px-3 py-1">
              {h.name} <span className="text-xs text-slate-500">({h.incomeAccountCode})</span>
            </li>
          ))}
        </ul>
        {manage && (
          <form
            noValidate
            aria-label={t('fees.addHead')}
            onSubmit={(e) => {
              e.preventDefault();
              if (headName.trim()) addHead.mutate(headName.trim());
            }}
            className="flex flex-wrap items-end gap-3"
          >
            <TextField
              label={t('fees.headName')}
              value={headName}
              onChange={(e) => setHeadName(e.target.value)}
            />
            <button type="submit" className={primaryButton} disabled={addHead.isPending}>
              {t('fees.addHead')}
            </button>
          </form>
        )}
      </div>

      <div className="space-y-3">
        <h2 className="text-lg font-semibold">{t('fees.tab.structure')}</h2>
        {items.data?.length === 0 && <p className="text-slate-600">{t('fees.noItems')}</p>}
        {items.data && items.data.length > 0 && (
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase text-slate-600">
              <tr>
                <th scope="col" className="p-2">
                  {t('fees.class')}
                </th>
                <th scope="col" className="p-2">
                  {t('fees.head')}
                </th>
                <th scope="col" className="p-2">
                  {t('fees.dueDate')}
                </th>
                <th scope="col" className="p-2 text-right">
                  {t('fees.amount')}
                </th>
                <th scope="col" className="p-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.data.map((i) => (
                <tr key={i.id}>
                  <td className="p-2">{i.className}</td>
                  <td className="p-2">{i.feeHeadName}</td>
                  <td className="p-2">{i.dueDate}</td>
                  <td className="p-2 text-right">{formatMinor(i.amountMinor, currency)}</td>
                  <td className="p-2 text-right">
                    {manage && (
                      <button
                        type="button"
                        className="text-xs text-red-700 underline"
                        aria-label={`${t('fees.removeItem')}: ${i.feeHeadName} ${i.className} ${i.dueDate}`}
                        onClick={() => removeItem.mutate(i.id)}
                      >
                        {t('fees.removeItem')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {manage && (
          <form
            noValidate
            aria-label={t('fees.addItem')}
            onSubmit={(e) => {
              e.preventDefault();
              const amountMinor = parseMajor(item.amount, currency);
              const next: Record<string, string> = {};
              if (!item.classId) next.classId = t('fees.pickStudent');
              if (!item.feeHeadId) next.feeHeadId = t('fees.pickStudent');
              if (amountMinor === null || amountMinor <= 0) next.amount = t('fees.invalidAmount');
              if (!item.dueDate) next.dueDate = t('fees.dueDate');
              setErrors(next);
              if (Object.keys(next).length > 0) return;
              addItem.mutate({
                classId: item.classId,
                feeHeadId: item.feeHeadId,
                amountMinor,
                dueDate: item.dueDate,
              });
            }}
            className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-5"
          >
            <div>
              <label htmlFor="item-class" className="block text-sm font-medium">
                {t('fees.class')}
              </label>
              <select
                id="item-class"
                value={item.classId}
                onChange={(e) => setItem({ ...item, classId: e.target.value })}
                className={selectClass}
              >
                <option value="">—</option>
                {classes.data?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              {errors.classId && (
                <p role="alert" className="text-xs text-red-700">
                  {errors.classId}
                </p>
              )}
            </div>
            <div>
              <label htmlFor="item-head" className="block text-sm font-medium">
                {t('fees.head')}
              </label>
              <select
                id="item-head"
                value={item.feeHeadId}
                onChange={(e) => setItem({ ...item, feeHeadId: e.target.value })}
                className={selectClass}
              >
                <option value="">—</option>
                {heads.data
                  ?.filter((h) => h.isActive)
                  .map((h) => (
                    <option key={h.id} value={h.id}>
                      {h.name}
                    </option>
                  ))}
              </select>
              {errors.feeHeadId && (
                <p role="alert" className="text-xs text-red-700">
                  {errors.feeHeadId}
                </p>
              )}
            </div>
            <TextField
              label={`${t('fees.amount')} (${currency})`}
              inputMode="decimal"
              value={item.amount}
              error={errors.amount}
              onChange={(e) => setItem({ ...item, amount: e.target.value })}
            />
            <TextField
              label={t('fees.dueDate')}
              type="date"
              value={item.dueDate}
              error={errors.dueDate}
              onChange={(e) => setItem({ ...item, dueDate: e.target.value })}
            />
            <div className="self-end">
              <button type="submit" className={primaryButton} disabled={addItem.isPending}>
                {t('fees.addItem')}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
